import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, useMemo } from 'react';
import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import { useNewsById, useSmartRelatedNews, usePopularNews, useCategories } from '@/hooks/useNews';
import CommentSection from './components/CommentSection';
import ReactionBar from './components/ReactionBar';
import { supabase } from '@/lib/supabase';
import { useSEO } from '@/hooks/useSEO';

function autoLinkUrls(html: string): string {
  return html.replace(
    /(?<!href=["']|src=["']|["'>])(https?:\/\/[^\s<>"')\]]+)/g,
    (url) => `<a href="${url}" target="_blank" rel="nofollow noopener noreferrer">${url}</a>`
  );
}

function newsHref(item: { id: string; slug?: string }): string {
  return item.slug ? `/haber/${item.slug}` : `/haber/${item.id}`;
}

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { newsItem: article, loading } = useNewsById(id || '');
  const { categories } = useCategories();

  // UUID ile açıldıysa slug'a redirect et
  useEffect(() => {
    if (!loading && article?.slug && id !== article.slug) {
      navigate(`/haber/${article.slug}`, { replace: true });
    }
  }, [article, loading, id, navigate]);
  const { news: smartRelated, loading: relatedLoading } = useSmartRelatedNews(
    article?.id || id || '',
    article?.category || '',
    article?.title || '',
    6
  );
  const { news: popularNews } = usePopularNews(5);
  const [readingProgress, setReadingProgress] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [minutesLeft, setMinutesLeft] = useState<number | null>(null);
  const articleRef = useRef<HTMLDivElement>(null);

  const category = article ? categories.find(c => c.id === article.category) : null;

  const processedContent = useMemo(() => {
    if (!article?.content) return '';
    return autoLinkUrls(article.content);
  }, [article?.content]);

  const relatedMain = smartRelated.slice(0, 3);
  const relatedSidebar = smartRelated.slice(0, 4);

  const canonicalSlug = article?.slug || article?.id || id;
  const canonicalUrl = `https://teknonovahub.com/haber/${canonicalSlug}`;

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    url: canonicalUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: 'TeknoNovaHub',
      logo: { '@type': 'ImageObject', url: 'https://teknonovahub.com/logo.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    keywords: `${category?.name}, teknoloji haberleri, ${article.author}`,
    articleSection: category?.name,
    inLanguage: 'tr',
  } : null;

  const breadcrumbSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://teknonovahub.com' },
      { '@type': 'ListItem', position: 2, name: category?.name || 'Kategori', item: `https://teknonovahub.com/kategori/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl },
    ],
  } : null;

  useSEO({
    title: article ? article.title : 'Haber Yükleniyor...',
    description: article?.excerpt,
    keywords: article ? `${category?.name}, teknoloji, ${article.author}, TeknoNovaHub` : undefined,
    image: article?.image,
    url: `/haber/${canonicalSlug}`,
    type: 'article',
    publishedTime: article?.date,
    modifiedTime: article?.date,
    author: article?.author,
    schema: articleSchema && breadcrumbSchema ? [articleSchema, breadcrumbSchema] : undefined,
  });

  useEffect(() => {
    if (!article) return;
    const text = article.content?.replace(/<[^>]+>/g, '') || '';
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    setMinutesLeft(Math.max(1, Math.ceil(wordCount / 200)));
  }, [article]);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = Math.min((window.scrollY / totalHeight) * 100, 100);
      setReadingProgress(pct);
      if (articleRef.current && article) {
        const text = article.content?.replace(/<[^>]+>/g, '') || '';
        const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
        const totalMinutes = Math.max(1, Math.ceil(wordCount / 200));
        setMinutesLeft(Math.max(0, Math.ceil(totalMinutes * (1 - pct / 100))));
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article]);

  // Increment views using article's actual UUID id
  useEffect(() => {
    if (article?.id) {
      supabase.rpc('increment_news_views', { news_id: article.id });
    }
  }, [article?.id]);

  useEffect(() => {
    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
    setIsBookmarked(bookmarks.includes(article?.id || id));
  }, [article?.id, id]);

  const toggleBookmark = () => {
    const key = article?.id || id || '';
    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
    if (isBookmarked) {
      localStorage.setItem('bookmarks', JSON.stringify(bookmarks.filter((b: string) => b !== key)));
    } else {
      localStorage.setItem('bookmarks', JSON.stringify([...bookmarks, key]));
    }
    setIsBookmarked(!isBookmarked);
  };

  const [copySuccess, setCopySuccess] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = article?.title || '';

  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer,width=600,height=400');
  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer,width=600,height=500');
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`, '_blank', 'noopener,noreferrer');
  const copyLink = () => navigator.clipboard.writeText(shareUrl).then(() => { setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-white/30 animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-news-line text-6xl text-white/20 mb-4" />
          <h1 className="text-white font-bold text-2xl mb-2">Haber Bulunamadı</h1>
          <Link to="/" className="text-[#00D9FF] hover:underline cursor-pointer">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <div className="fixed top-0 left-0 right-0 h-1 bg-white/10 z-50">
        <div className="h-full bg-gradient-to-r from-[#00D9FF] to-[#00FF88] transition-all duration-150" style={{ width: `${readingProgress}%` }} />
      </div>

      {article && minutesLeft !== null && (
        <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
          <div className="flex items-center gap-2.5 bg-[#0A0E27]/90 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-2.5">
            <div className="relative w-8 h-8 flex-shrink-0">
              <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="13" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                <circle cx="16" cy="16" r="13" fill="none" stroke="url(#progressGrad)" strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 13}`}
                  strokeDashoffset={`${2 * Math.PI * 13 * (1 - readingProgress / 100)}`}
                  className="transition-all duration-300"
                />
                <defs>
                  <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00D9FF" /><stop offset="100%" stopColor="#00FF88" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[8px] font-black text-white">{Math.round(readingProgress)}%</span>
            </div>
            <div>
              {minutesLeft === 0 ? (
                <div className="text-[#00FF88] text-xs font-bold flex items-center gap-1"><i className="ri-check-double-line" /> Tamamlandı!</div>
              ) : (
                <><div className="text-white text-xs font-bold leading-none">~{minutesLeft} dk kaldı</div><div className="text-white/30 text-[10px] mt-0.5">okuma süresi</div></>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col items-center gap-2">
        <div className="flex flex-col items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-2">
          <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase rotate-180" style={{ writingMode: 'vertical-rl' }}>Paylaş</span>
          <div className="w-px h-4 bg-white/10" />
          <button onClick={shareTwitter} className="w-9 h-9 flex items-center justify-center bg-[#1DA1F2]/10 text-[#1DA1F2] rounded-xl hover:bg-[#1DA1F2]/30 hover:scale-110 transition-all cursor-pointer"><i className="ri-twitter-x-line text-sm" /></button>
          <button onClick={shareLinkedIn} className="w-9 h-9 flex items-center justify-center bg-[#0A66C2]/10 text-[#0A66C2] rounded-xl hover:bg-[#0A66C2]/30 hover:scale-110 transition-all cursor-pointer"><i className="ri-linkedin-line text-sm" /></button>
          <button onClick={shareWhatsApp} className="w-9 h-9 flex items-center justify-center bg-[#25D366]/10 text-[#25D366] rounded-xl hover:bg-[#25D366]/30 hover:scale-110 transition-all cursor-pointer"><i className="ri-whatsapp-line text-sm" /></button>
          <button onClick={copyLink} className={`w-9 h-9 flex items-center justify-center rounded-xl hover:scale-110 transition-all cursor-pointer ${copySuccess ? 'bg-[#00FF88]/20 text-[#00FF88]' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}><i className={`${copySuccess ? 'ri-check-line' : 'ri-links-line'} text-sm`} /></button>
          <div className="w-px h-4 bg-white/10" />
          <button onClick={toggleBookmark} className={`w-9 h-9 flex items-center justify-center rounded-xl hover:scale-110 transition-all cursor-pointer ${isBookmarked ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}><i className={`${isBookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-sm`} /></button>
        </div>
      </div>

      <NewsNavbar />
      
      <main className="pt-24 md:pt-28">
        <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27] via-[#0A0E27]/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-5xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-white/60 mb-4">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/kategori/${article.category}`} className="hover:text-white transition-colors cursor-pointer">{category?.name}</Link>
              </nav>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full mb-4" style={{ backgroundColor: `${category?.color}20`, color: category?.color, border: `1px solid ${category?.color}40` }}>
                <i className={`${category?.icon} text-xs`} />{category?.name}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">{article.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-full"><i className="ri-user-line text-white/70" /></div>
                  <div><div className="text-white font-medium">{article.author}</div><div className="text-xs">Yazar</div></div>
                </div>
                <div className="w-px h-8 bg-white/20" />
                <div className="flex items-center gap-1.5"><i className="ri-calendar-line" /><span>{article.date}</span></div>
                <div className="flex items-center gap-1.5"><i className="ri-time-line" /><span>{article.read_time} okuma</span></div>
                <div className="flex items-center gap-1.5"><i className="ri-eye-line" /><span>{article.views?.toLocaleString()} görüntüleme</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <article ref={articleRef} className="lg:col-span-2">
              <div className="text-xl text-white/80 font-medium leading-relaxed mb-8 pb-8 border-b border-white/10">{article.excerpt}</div>
              <div className="article-content" dangerouslySetInnerHTML={{ __html: processedContent }} />

              <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-white/10">
                <span className="text-white/40 text-sm mr-2">Etiketler:</span>
                {['Teknoloji', category?.name, 'Haber'].filter(Boolean).map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-white/5 text-white/60 text-xs rounded-full border border-white/10">#{tag}</span>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pt-8 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <span className="text-white/50 text-sm font-medium">Paylaş:</span>
                  <button onClick={shareTwitter} className="w-9 h-9 flex items-center justify-center bg-[#1DA1F2]/10 text-[#1DA1F2] rounded-full hover:bg-[#1DA1F2]/30 hover:scale-110 transition-all cursor-pointer"><i className="ri-twitter-x-line" /></button>
                  <button onClick={shareLinkedIn} className="w-9 h-9 flex items-center justify-center bg-[#0A66C2]/10 text-[#0A66C2] rounded-full hover:bg-[#0A66C2]/30 hover:scale-110 transition-all cursor-pointer"><i className="ri-linkedin-line" /></button>
                  <button onClick={shareWhatsApp} className="w-9 h-9 flex items-center justify-center bg-[#25D366]/10 text-[#25D366] rounded-full hover:bg-[#25D366]/30 hover:scale-110 transition-all cursor-pointer"><i className="ri-whatsapp-line" /></button>
                  <button onClick={copyLink} className={`w-9 h-9 flex items-center justify-center rounded-full hover:scale-110 transition-all cursor-pointer ${copySuccess ? 'bg-[#00FF88]/20 text-[#00FF88]' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}><i className={copySuccess ? 'ri-check-line' : 'ri-links-line'} /></button>
                  {copySuccess && <span className="text-[#00FF88] text-xs font-medium animate-pulse">Kopyalandı!</span>}
                </div>
                <button onClick={toggleBookmark} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${isBookmarked ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/30' : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'}`}>
                  <i className={isBookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />{isBookmarked ? 'Kaydedildi' : 'Kaydet'}
                </button>
              </div>

              <ReactionBar newsId={article.id} />

              <div className="mt-10 p-6 bg-white/5 border border-white/10 rounded-2xl">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#00D9FF]/10 rounded-full flex-shrink-0"><i className="ri-user-line text-[#00D9FF] text-2xl" /></div>
                  <div>
                    <h4 className="text-white font-bold text-lg">{article.author}</h4>
                    <p className="text-white/50 text-sm mt-1">TeknoNovaHub teknoloji editörü. Yapay zeka, mobil teknolojiler ve oyun dünyasındaki gelişmeleri takip ediyor.</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-10 border-t border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: category?.color || '#00D9FF' }} />
                  <h3 className="text-white font-black text-xl">İlgili Haberler</h3>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${category?.color}15`, color: category?.color }}>{category?.name}</span>
                  <span className="text-xs text-white/30 ml-auto flex items-center gap-1"><i className="ri-sparkling-line" />Akıllı Öneri</span>
                </div>
                {relatedLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (<div key={i} className="rounded-2xl overflow-hidden animate-pulse"><div className="h-40 bg-white/5" /><div className="p-4 space-y-2"><div className="h-3 bg-white/5 rounded w-3/4" /><div className="h-3 bg-white/5 rounded w-full" /></div></div>))}
                  </div>
                ) : relatedMain.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedMain.map((item) => {
                      const itemCat = categories.find(c => c.id === item.category);
                      const relColor = itemCat?.color || category?.color || '#00D9FF';
                      const isSameCat = item.category === article.category;
                      return (
                        <Link key={item.id} to={newsHref(item)} className="group bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.07] hover:border-white/20 transition-all cursor-pointer block">
                          <div className="relative w-full h-40 overflow-hidden">
                            <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <div className="absolute top-2 left-2">
                              {!isSameCat && <span className="px-2 py-0.5 text-[9px] font-bold rounded-full" style={{ backgroundColor: `${relColor}30`, color: relColor, border: `1px solid ${relColor}50` }}>{itemCat?.name}</span>}
                            </div>
                            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                              <span className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded-full bg-black/50 text-white/80 backdrop-blur-sm"><i className="ri-time-line" />{item.read_time}</span>
                              <span className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded-full bg-black/50 text-white/80 backdrop-blur-sm"><i className="ri-eye-line" />{item.views >= 1000 ? `${(item.views / 1000).toFixed(1)}K` : item.views}</span>
                            </div>
                          </div>
                          <div className="p-4">
                            <h4 className="text-white/80 text-sm font-semibold leading-snug line-clamp-2 group-hover:text-white transition-colors mb-2">{item.title}</h4>
                            <p className="text-white/40 text-xs line-clamp-2 mb-3">{item.excerpt}</p>
                            <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: relColor }}>Haberi Oku <i className="ri-arrow-right-line" /></div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="mt-10"><CommentSection newsId={article.id} /></div>
            </article>

            <aside className="lg:col-span-1 space-y-6">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-5"><i className="ri-fire-line text-[#FF6B6B] text-xl" /><h3 className="text-white font-bold">Trend Haberler</h3></div>
                <div className="flex flex-col gap-4">
                  {popularNews.slice(0, 5).map((item, i) => (
                    <Link key={item.id} to={newsHref(item)} className="flex items-start gap-3 group cursor-pointer">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-white/5 text-[#00D9FF] text-xs font-bold rounded-full">{i + 1}</span>
                      <span className="text-white/60 text-sm leading-snug group-hover:text-white transition-colors line-clamp-2">{item.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#00D9FF]/10 to-[#00FF88]/10 border border-white/10 rounded-2xl p-5">
                <i className="ri-mail-send-line text-3xl text-[#00D9FF] mb-3 block" />
                <h3 className="text-white font-bold text-lg mb-2">Bültene Abone Ol</h3>
                <p className="text-white/50 text-sm mb-4">Günlük teknoloji haberleri e-posta kutunuzda.</p>
                <Link to="/abonelik" className="block w-full text-center py-2.5 bg-[#00D9FF] text-[#0A0E27] font-bold rounded-xl hover:bg-[#00D9FF]/90 transition-colors cursor-pointer text-sm">Hemen Abone Ol</Link>
              </div>

              {relatedSidebar.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4"><h3 className="text-white font-bold flex-1">İlgili Haberler</h3><i className="ri-sparkling-line text-white/30 text-sm" /></div>
                  <div className="flex flex-col gap-4">
                    {relatedSidebar.map((item) => {
                      const itemCat = categories.find(c => c.id === item.category);
                      const relColor = itemCat?.color || '#00D9FF';
                      return (
                        <Link key={item.id} to={newsHref(item)} className="group cursor-pointer">
                          <div className="aspect-[16/10] rounded-xl overflow-hidden mb-2 relative">
                            <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                            {item.category !== article.category && <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full" style={{ backgroundColor: `${relColor}30`, color: relColor }}>{itemCat?.name}</span>}
                          </div>
                          <h4 className="text-white/70 text-sm font-medium line-clamp-2 group-hover:text-[#00D9FF] transition-colors">{item.title}</h4>
                          <div className="flex items-center gap-2 mt-1 text-white/30 text-xs">
                            <i className="ri-eye-line" /><span>{item.views >= 1000 ? `${(item.views / 1000).toFixed(1)}K` : item.views}</span><span>·</span><span>{item.read_time}</span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </main>
      <NewsFooter />
    </div>
  );
}
